import { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Bell, BellOff, Check, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useNavigate } from 'react-router';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/sonner';
import { useAuth, useLogout, useRestoreAdmin, isSuperAdmin, adminSnapshotAtom } from '@/hooks/use-auth';
import { useUserAccounts } from '@/hooks/use-user-accounts';
import { useBusinessUnit, useBusinessUnitsByAccount } from '@/hooks';
import { isSentinelRoleAtom, routingEnabledAtom, useUnassignedCounts } from '@/hooks/use-sentinel-routing';
import { resolvedBusinessUnitIdAtom } from '@/hooks/use-business-units';
import { useSentinelAvailability, type SentinelAvailability } from '@/hooks/use-sentinel-availability';
import { useSentinelAlertSettings, type AssignmentNotifications } from '@/hooks/use-sentinel-assignment-notifications';
import { useSwitchAccount } from '@/hooks/use-switch-account';
import { resetSimulatorAtom } from '@/stores/websocket.store';
import { endSimulatorSessionAtom } from '@/modules/simulator/session/simulator-session.store';
import { cn } from '@/lib/utils';
import { accountMenuOrder, opensBrandPanel, visibleBrands } from './account-menu';
import { PANEL, ROW, ROW_HOVER, ROW_INNER, ROW_SELECTED, SECTION_LABEL } from './menu-styles';
import type { BusinessUnit } from '@/types';

interface SidebarUserMenuProps {
	showAccountSwitcher?: boolean;
}


const SEARCH_FROM = 10;

/** Subscribes to the accounts query so it loads with the page; renders nothing. */
function WarmUserAccounts() {
	useUserAccounts();
	return null;
}

export function SidebarUserMenu({ showAccountSwitcher = false }: SidebarUserMenuProps) {
	const navigate = useNavigate();
	const logoutMutation = useLogout();
	const { userInitials, displayName, currentUser } = useAuth();
	const [adminSnapshot] = useAtom(adminSnapshotAtom);
	const restoreAdmin = useRestoreAdmin();
	const resetSimulator = useSetAtom(resetSimulatorAtom);
	const endSimulatorSession = useSetAtom(endSimulatorSessionAtom);
	const initials = userInitials || displayName?.slice(0, 2) || currentUser?.email?.slice(0, 2) || '';

	// Both switchers live inside the menu content, which unmounts while the menu is closed —
	// so their queries only started when someone OPENED the menu, and the brand and account
	// lists visibly filled in afterwards. Subscribing here, in the always-mounted trigger,
	// starts them with the page instead: by the time the menu is opened the shared query
	// atoms are already resolved and it renders complete.
	//
	// Not a prefetch call — reading the same atoms the children read is what warms them, so
	// there is no second request and no cache key to keep in step. Both are auth-gated and
	// 5-minute stale, so this costs one request per session, not one per open.
	//
	// `useBusinessUnit`, not `useBusinessUnits`: the singular one also RESOLVES the stored
	// brand against the account's list and writes the answer back. That resolution used to
	// happen only inside the brand submenu, which mounts on hover — so after an account
	// switch the brand stayed null until someone opened this menu, and every brand-scoped
	// read answered account-wide until then. Running it here means the brand is settled
	// before the first scoped query goes out.
	useBusinessUnit();

	// Read once in the always-mounted trigger, so the avatar's dot and the menu rows share one state.
	const isSentinel = useAtomValue(isSentinelRoleAtom);
	const routingEnabled = useAtomValue(routingEnabledAtom);
	const showStatus = isSentinel && routingEnabled;
	const statusBuId = useAtomValue(resolvedBusinessUnitIdAtom);
	const availability = useSentinelAvailability(showStatus ? statusBuId : null);
	const alerts = useSentinelAlertSettings();

	const handleLogout = async () => {
		// Decide the login route before state is cleared: admins (incl. while
		// impersonating) go back to the super admin login, everyone else to /login.
		const loginRoute = isSuperAdmin(currentUser) || adminSnapshot ? '/super_admin/login' : '/login';
		try {
			await logoutMutation.mutateAsync();
			// Nothing reloads on logout, so the simulator (thread, profile, the scenario on
			// screen) is still in memory and must not reach the next login.
			resetSimulator();
			endSimulatorSession();
			toast.success('Logged out successfully');
			navigate(loginRoute);
		} catch {
			toast.error('Failed to logout. Please try again.');
		}
	};

	const handleBackToAdmin = () => {
		// The account context changes, so the simulator scenario goes with it.
		resetSimulator();
		// Full reload (like account switching) so all state — incl. the WebSocket —
		// re-initializes for the restored admin session.
		if (restoreAdmin()) {
			window.location.href = '/super-admin/accounts';
		}
	};

	return (
		<DropdownMenu>
			{/* Warms the account list on page load, for the same reason as `useBusinessUnits`
			    above. A component rather than a bare hook call because it is conditional: the
			    super-admin layout renders this menu WITHOUT the account switcher, and fetching
			    a list that surface never shows would trade one stall for one wasted request. */}
			{showAccountSwitcher && <WarmUserAccounts />}
			<div className='flex w-full px-1 group-data-[collapsible=icon]:justify-center'>
				<DropdownMenuTrigger asChild>
					<button
						aria-label='Account menu'
						className='relative flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-gray-100 text-[13px] leading-[18px] font-normal text-gray-900 uppercase transition-colors hover:bg-gray-200'
					>
						{initials}
						{showStatus && availability.online !== null && (
							<span
								aria-hidden
								className={cn(
									'absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-white',
									availability.online ? 'bg-green-500' : 'bg-gray-400'
								)}
							/>
						)}
					</button>
				</DropdownMenuTrigger>
			</div>
			<DropdownMenuContent
				side='right'
				align='end'
				// Focus the current account: Radix focuses the first row, which closes its brand panel.
				onOpenAutoFocus={(event: Event) => {
					// Found in the DOM, not a ref: the row only becomes a sub-trigger once brands load.
					// Matched on the current account rather than on the first sub-trigger in the
					// panel, which belongs to another account when this one has a single brand.
					const trigger = (event.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>(
						'[data-current-account="true"][data-slot="dropdown-menu-sub-trigger"]'
					);
					if (!trigger) return;
					event.preventDefault();
					trigger.focus();
				}}
				// 8px clear of the rail: 8 past the avatar, which sits 8 inside it.
				sideOffset={16}
				alignOffset={-10}
				collisionPadding={8}
				className={cn(
					PANEL,
					'max-h-(--radix-dropdown-menu-content-available-height) w-[232px] min-w-0 overflow-hidden border-gray-300 p-[6px] shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)]'
				)}
			>
				<div className='flex shrink-0 items-end gap-[8px]'>
					<div className='flex size-[36px] shrink-0 items-center justify-center overflow-clip rounded-full border border-gray-300 bg-gray-100 text-[14px] leading-[20px] font-normal text-gray-900 uppercase'>
						{initials}
					</div>
					<div className='flex min-w-0 flex-1 flex-col'>
						<p className='truncate text-[13px] leading-[18px] font-medium text-ink-strong'>{displayName}</p>
						<p className='truncate text-[13px] leading-[18px] font-normal text-gray-800'>{currentUser?.email}</p>
						{adminSnapshot && (
							<span className='mt-1 inline-block w-fit rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-600'>
								Impersonating
							</span>
						)}
					</div>
				</div>

				<Divider />

				{showStatus && (
					<>
						<SentinelStatusSection availability={availability} alerts={alerts} />
						<Divider />
					</>
				)}

				<AccountSection showAccountSwitcher={showAccountSwitcher} />

				{adminSnapshot && (
					<>
						<Divider />
						<DropdownMenuItem
							onSelect={handleBackToAdmin}
							className={cn(ROW, ROW_HOVER, 'p-[1px] text-blue-600 focus:text-blue-600')}
						>
							<span className={ROW_INNER}>
								<ArrowLeft className='size-[14px] shrink-0' />
								Back to Admin
							</span>
						</DropdownMenuItem>
					</>
				)}

				<Divider />

				<AppearanceSection />

				<Divider />

				<DropdownMenuItem
					onSelect={(event) => {
						// Keep the menu open while the request is in flight: closing it first
						// leaves the operator looking at the app with no sign anything happened.
						event.preventDefault();
						void handleLogout();
					}}
					disabled={logoutMutation.isPending}
					className='flex h-[28px] w-full shrink-0 items-center gap-[8px] overflow-clip rounded-[100px] bg-white px-[12px] py-[4px] text-[13px] leading-[18px] font-normal text-red-500 outline-hidden focus:bg-red-50 focus:text-red-500 data-disabled:cursor-not-allowed data-disabled:opacity-50'
				>
					<LogOut className='size-[14px] shrink-0 !text-red-500' strokeWidth={2.4} />
					{logoutMutation.isPending ? 'Logging out...' : 'Logout'}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

const THEMES = [
	{ value: 'light', label: 'Light', icon: Sun },
	{ value: 'dark', label: 'Dark', icon: Moon },
	{ value: 'system', label: 'System', icon: Monitor }
] as const;

/** Light / dark / follow-the-OS, as a three-way segment under the same heading style as the lists above. */
function AppearanceSection() {
	const { theme = 'system', setTheme } = useTheme();
	return (
		<div className='flex w-full shrink-0 flex-col gap-[6px]'>
			<p className={SECTION_LABEL}>Appearance</p>
			<div role='radiogroup' aria-label='Appearance' className='flex w-full gap-[2px] rounded-[10px] bg-ink/4 p-[2px]'>
				{THEMES.map(({ value, label, icon: Icon }) => {
					const selected = theme === value;
					return (
						<button
							key={value}
							type='button'
							role='radio'
							aria-checked={selected}
							title={label}
							onClick={() => setTheme(value)}
							className={cn(
								'flex h-[24px] flex-1 items-center justify-center gap-[6px] rounded-[8px] text-[12px] leading-[16px] text-gray-800 outline-hidden transition-colors hover:text-ink focus-visible:ring-2 focus-visible:ring-ring',
								selected && 'bg-white text-ink shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)]'
							)}
						>
							<Icon className='size-[13px] shrink-0' strokeWidth={2} />
							{label}
						</button>
					);
				})}
			</div>
		</div>
	);
}

const STATUSES = [
	{ online: true, label: 'Online', dot: 'bg-green-500' },
	{ online: false, label: 'Offline', dot: 'bg-gray-400' }
] as const;

/** A Sentinel's Online/Offline for the current brand, and one Notifications switch (sound and Chrome alerts). */
export function SentinelStatusSection({ availability, alerts }: { availability: SentinelAvailability; alerts: AssignmentNotifications }) {
	const { online, isSaving, setOnline } = availability;
	// Off until Chrome has been asked, so turning them on is what asks it.
	const soundOn = !alerts.soundMuted && alerts.permission !== 'default';
	return (
		<div role='group' aria-label='Set yourself as' className='flex w-full shrink-0 flex-col gap-[2px]'>
			<p className={cn(SECTION_LABEL, 'pb-[4px]')}>{online === null ? 'Checking status…' : 'Set yourself as'}</p>
			{STATUSES.map((status) => {
				const selected = online === status.online;
				return (
					<DropdownMenuItem
						key={status.label}
						role='menuitemradio'
						aria-checked={selected}
						disabled={online === null || isSaving}
						onSelect={(event) => {
							event.preventDefault();
							if (!selected) void setOnline(status.online);
						}}
						className={cn(ROW, ROW_HOVER, selected && ROW_SELECTED)}
					>
						<span className={ROW_INNER}>
							<span aria-hidden className={cn('size-2 shrink-0 rounded-full', status.dot)} />
							<span className='flex-1'>{status.label}</span>
							{selected && <Check className='size-[14px] shrink-0' />}
						</span>
					</DropdownMenuItem>
				);
			})}
			<DropdownMenuItem
				role='menuitemcheckbox'
				aria-checked={soundOn}
				onSelect={(event) => {
					event.preventDefault();
					if (!soundOn && alerts.permission === 'default') void alerts.requestPermission();
					alerts.setSoundMuted(soundOn);
				}}
				className={cn(ROW, ROW_HOVER)}
			>
				<span className={ROW_INNER}>
					<Bell className='size-[14px] shrink-0' />
					<span className='flex-1'>Notifications</span>
					{/* Drawn, not a Switch: the whole row is the control, and a button inside it would be a second one. */}
					<span
						aria-hidden
						className={cn(
							'flex h-[16px] w-[28px] shrink-0 items-center rounded-full p-[2px] transition-colors',
							soundOn ? 'bg-indigo-500' : 'bg-gray-600'
						)}
					>
						<span className={cn('size-3 rounded-full bg-white transition-transform', soundOn && 'translate-x-3')} />
					</span>
				</span>
			</DropdownMenuItem>
			{soundOn && alerts.permission === 'denied' && (
				<p className='flex items-start gap-[6px] px-[10px] py-[4px] text-[11px] leading-4 text-gray-800'>
					<BellOff className='mt-0.5 size-3 shrink-0' aria-hidden />
					Notifications are blocked. Allow them for this site in Chrome and, on a Mac, in System Settings → Notifications.
				</p>
			)}
		</div>
	);
}

/** The full-width hairline between sections. */
function Divider() {
	return <div className='h-px w-full shrink-0 bg-gray-300' />;
}

/**
 * The account list. The one you are in is marked, and opens its brands on hover.
 *
 * ONE ROW WHEN THERE IS ONE ACCOUNT. The heading and the row stay even then, because the
 * row is how the brand list is reached — it is not a switcher that happens to have one
 * option, it is the parent of the thing underneath it.
 *
 * A CHEVRON MEANS A CHOICE OF BRAND. One brand is the whole account, so that row switches
 * straight to it; only an account with more than one opens a panel.
 */
function AccountSection({ showAccountSwitcher }: { showAccountSwitcher: boolean }) {
	const { accounts, currentAccountId } = useUserAccounts();
	const { switchAccount, isSwitching } = useSwitchAccount();
	const { byAccount } = useBusinessUnitsByAccount();
	const { buId } = useBusinessUnit();
	const [query, setQuery] = useState('');
	// Held here, not per row, so a scroll can close it — Radix anchors the panel to its row.
	const [openAccountId, setOpenAccountId] = useState<number | null>(null);

	const rootRef = useRef<HTMLDivElement>(null);
	const [settled, setSettled] = useState(false);

	// Rows move while the panel slides in, so opening now would anchor mid-flight and jump.
	useEffect(() => {
		const content = rootRef.current?.closest('[data-slot="dropdown-menu-content"]');
		const done = () => setSettled(true);
		content?.addEventListener('animationend', done, { once: true });
		const fallback = window.setTimeout(done, 300);
		return () => {
			content?.removeEventListener('animationend', done);
			window.clearTimeout(fallback);
		};
	}, []);

	// An effect, not an initial value: Radix's MenuSub closes itself on mount under StrictMode.
	useEffect(() => {
		if (settled) setOpenAccountId(currentAccountId);
	}, [settled, currentAccountId]);

	const visible = accountMenuOrder(accounts, currentAccountId, showAccountSwitcher);

	const searchable = visible.length > SEARCH_FROM;
	const needle = query.trim().toLowerCase();
	const matches = needle ? visible.filter((account) => account.name.toLowerCase().includes(needle)) : visible;

	return (
		<div ref={rootRef} className='flex min-h-0 w-full flex-1 flex-col gap-[10px]'>
			<div className='flex w-full shrink-0 items-center'>
				<p className={SECTION_LABEL}>Account</p>
			</div>
			{searchable ? (
				<input
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					// Radix's typeahead would otherwise swallow these keystrokes to jump between rows.
					onKeyDown={(event) => event.stopPropagation()}
					placeholder='Search accounts'
					aria-label='Search accounts'
					className='h-[28px] w-full shrink-0 rounded-[10px] border border-gray-300 bg-white px-[10px] text-[13px] leading-[18px] text-ink outline-none placeholder:text-gray-700'
				/>
			) : null}
			{/* max-h-52 is seven 28px rows 2px apart; past that the list scrolls. The extra 4px of
			    width pulls the scrollbar out over the panel's padding, 2px short of its edge. */}
			<div
				onScroll={() => setOpenAccountId(null)}
				className={cn(
					`flex max-h-52 min-h-0 w-[calc(100%+4px)] flex-1 flex-col gap-[2px] overflow-y-auto pr-[6px]
					 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent
					 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-500`,
					// Held open while searching, so typing narrows the list without resizing the panel.
					searchable && 'min-h-52'
				)}
			>
				{matches.map((account) => {
					const isCurrent = account.id === currentAccountId;
					// Only the account you are in has a brand ticked.
					const options = visibleBrands(byAccount[String(account.id)] ?? [], isCurrent ? buId : null);

					if (!opensBrandPanel(options)) {
						return (
							<DropdownMenuItem
								key={account.id}
								// Not `disabled` on the current row: Radix greys a disabled item.
								disabled={isSwitching && !isCurrent}
								onSelect={(event) => {
									if (isCurrent) {
										event.preventDefault();
										return;
									}
									void switchAccount(account.id);
								}}
								className={cn(
									ROW,
									ROW_HOVER,
									'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
									isCurrent ? cn(ROW_SELECTED, 'cursor-default') : 'cursor-pointer'
								)}
							>
								<span className={ROW_INNER}>
									<span className='min-w-px flex-1 truncate text-left'>{account.name}</span>
								</span>
							</DropdownMenuItem>
						);
					}

					return (
						<AccountRow
							key={account.id}
							accountId={account.id}
							label={account.name}
							options={options}
							isCurrent={isCurrent}
							open={openAccountId === account.id}
							onOpenChange={(next) => setOpenAccountId(next ? account.id : null)}
						/>
					);
				})}
				{matches.length === 0 ? (
					<p className='flex flex-1 items-center justify-center px-[10px] text-center text-xs leading-4 text-gray-800'>
						No accounts found
					</p>
				) : null}
			</div>
		</div>
	);
}

/** An account with brands: marked when it is the one you are in, and opening them to the side. */
function AccountRow({
	accountId,
	label,
	options,
	isCurrent,
	open,
	onOpenChange
}: {
	accountId: number;
	label: string;
	options: BusinessUnit[];
	isCurrent: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	return (
		<DropdownMenuSub open={open} onOpenChange={onOpenChange}>
			<DropdownMenuSubTrigger
				data-current-account={isCurrent}
				className={cn(
					ROW,
					isCurrent && ROW_SELECTED,
					// Same pin as ROW_HOVER: this row is a SubTrigger, which greys its label
					// while the submenu is open unless told otherwise.
					'focus:text-ink data-[state=open]:text-ink',
					!isCurrent && ROW_HOVER,
					// The wrapper paints un-classed icons grey; `!` beats it, and Figma wants body colour.
					'[&>svg]:!text-ink',
					// Only `pr` here, for the chevron the wrapper adds; `py` would merge away ROW's ring.
					'pr-[6px]'
				)}
			>
				<span className='flex min-w-px flex-1 items-center gap-[8px] py-[4px] pl-[10px]'>
					<span className='min-w-px flex-1 truncate text-left'>{label}</span>
				</span>
			</DropdownMenuSubTrigger>
			<BrandSubmenu accountId={accountId} options={options} isCurrent={isCurrent} />
		</DropdownMenuSub>
	);
}

/**
 * The global brand scope, reached by hovering the account it belongs to.
 *
 * It used to be a flat section of this menu, and before that a dedicated top bar that cost
 * every page a 48px row to hold one select. Nesting it under the account says the true
 * thing about it: a brand belongs to ONE account, so the two are parent and child rather
 * than two sibling lists that happen to sit together.
 *
 * SHOWN ON EVERY PAGE, with no path gate. The trade-off that gate was protecting still
 * exists — a page that does not scope its reads shows the same rows whichever brand is
 * picked. Treat this as "which brand am I working as" rather than "this page is filtered",
 * and scope a section's reads when it needs to be filtered.
 */
function BrandSubmenu({
	accountId,
	options,
	isCurrent
}: {
	accountId: number;
	options: BusinessUnit[];
	isCurrent: boolean;
}) {
	const navigate = useNavigate();
	const { buId, setBusinessUnit } = useBusinessUnit();
	const { switchAccount } = useSwitchAccount();
	// Sentinel tickets waiting for an owner, per brand of the current account (routing only).
	const unassignedCounts = useUnassignedCounts();

	// Changing the brand sends you home, the way switching ACCOUNT already does.
	//
	// The page you were on belongs to the brand you just left: a workflow detail, a source,
	// a SOP are all addressed by an id the new brand may not own, and the ones that are
	// lists would sit there refetching under a heading that no longer describes them. Going
	// home means every screen after the switch was loaded under the brand it is showing.
	//
	// Client-side navigation, not a reload: the atom already holds the new brand and every
	// scoped query carries it in its key, so the destination fetches correctly on arrival.
	//
	// Only on an actual change — re-picking the current brand is a no-op in
	// `setBusinessUnit`, and it should not bounce you off the page either.
	const select = (unit: BusinessUnit) => {
		// Handed to the switch rather than stored first: switching clears the selected brand,
		// so it can only be applied on the far side of that.
		if (!isCurrent) {
			void switchAccount(accountId, '/', unit.id);
			return;
		}

		const changed = unit.id !== buId;
		setBusinessUnit(unit.id);
		if (changed) {
			// Named, and only on an actual change. Switching a brand sends you home and
			// refetches every scoped list, so without a word the screen simply changes
			// underneath you — the same disorientation the account switch avoids. The BRAND
			// NAME is the payload: the ids are meaningless to an operator, and "switched"
			// alone does not say which of several brands you landed on.
			toast.success(`Now viewing ${unit.name}`);
			navigate('/');
		}
	};

	return (
		<DropdownMenuSubContent
			// Lifts the panel so its bottom lines up with its row's. Radix pins sub-menus to
			// `align='start'`, so the offset is the only lever, and the panel has no height to
			// measure yet — so it is counted: 37px of chrome plus 30px a row, less the 28px row
			// it hangs off, which leaves 9 and a pixel of rounding.
			alignOffset={-(10 + 30 * options.length)}
			// Measured from the trigger row, which sits 13px inside the panel; 18 - 13 leaves 5px.
			sideOffset={18}
			collisionPadding={12}
			className={cn(
				PANEL,
				// Capped to what Radix measures as free on screen, and scrolled inside. An
				// account with a dozen brands ran off the bottom of the viewport otherwise:
				// the panel had no height of its own, so the list just grew.
				'max-h-(--radix-dropdown-menu-content-available-height) min-w-[147px] max-w-[232px] gap-[10px] overflow-hidden border-gray-300 p-[6px] shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)]'
			)}
		>
			<div className='flex w-full shrink-0 items-center'>
				<p className={SECTION_LABEL}>My Brands</p>
			</div>
			{/* `min-h-0` is what lets this shrink below its content and scroll — without it a
			    flex child keeps its full height and the cap above does nothing. */}
			<div className='flex min-h-0 w-full flex-1 flex-col gap-[2px] overflow-y-auto'>
				{options.map((unit) => {
					const isActive = isCurrent && unit.id === buId;
					const waiting = isCurrent ? (unassignedCounts[unit.id] ?? 0) : 0;
					return (
						<DropdownMenuItem
							key={unit.id}
							onSelect={() => select(unit)}
							className={cn(
								ROW,
								// Selected last, so the heavier layer wins while the pointer is on
								// the brand you are already scoped to rather than lightening it.
								ROW_HOVER,
								'cursor-pointer',
								isActive && ROW_SELECTED
							)}
						>
							{/* min-w-px twice: without it a flex child will not shrink and `truncate` does nothing. */}
							<span className='flex min-w-px flex-1 items-center justify-between gap-[8px] py-[4px] pr-[6px] pl-[10px]'>
								<span className='min-w-px truncate text-left'>
									{unit.name}
									{unit.status === 'inactive' ? ' (inactive)' : ''}
								</span>
								{waiting > 0 && (
									<span
										className='shrink-0 rounded-full bg-gray-100 px-1.5 text-[11px] text-gray-800'
										title={`${waiting} Sentinel ticket${waiting === 1 ? '' : 's'} waiting for an owner`}
									>
										{waiting} waiting
									</span>
								)}
								{isActive && <Check className='size-[16px] shrink-0 text-ink' />}
							</span>
						</DropdownMenuItem>
					);
				})}
			</div>
		</DropdownMenuSubContent>
	);
}
